import { styled } from 'goober';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type StateUpdater,
} from 'preact/hooks';

import { useIsMobile, useSessionStore } from '~/utils/generalHooks';
import {
  getWidgetAppParams,
  resolveIframePath,
  resolveLinkTargetSetting,
  shouldUseJwtAuth,
} from '~/utils';
import { getClientEmbedderInfo } from '~/utils/config';
import { getWidgetZIndexLayers } from '~/utils/zIndexLayers';

import { ChatWindow } from './ChatWindow';
import { FabButton } from './FabButton';
import { NudgeBox } from './NudgeBox';

import type {
  EmbedderInfo,
  LoadWidgetResponse,
  NudgeInfo,
  WidgetAppParams,
  WorknetWidgetConfig,
} from '@worknet/models';

type WidgetAPI = {
  open: (options?: { message?: string }) => void;
  close: () => void;
};

type WidgetViewsProps = {
  widgetConfig: WorknetWidgetConfig;
  widgetAppParams: WidgetAppParams;
  embedderInfo: EmbedderInfo;
  nudgeInfo?: NudgeInfo;
  nudgeId?: string;
  state: WidgetState;
  setState: Dispatch<StateUpdater<WidgetState>>;
  onOpenRef?: { current: ((message?: string) => void) | undefined };
  onCloseRef?: { current: (() => void) | undefined };
  authToken?: string;
  hostElement: HTMLElement;
};

type WorknetWidgetSessionData = {
  initialLoadId: string;
  lastLoadId: string;
  nudgeInfo?: NudgeInfo;
  nudgeId?: string;
  widgetAppParams: WidgetAppParams;
};

type WidgetMainState = 'initial' | 'chat-open' | 'chat-minimized';

type TransitionState =
  | { type: 'idle' }
  | { type: 'opening-chat'; from: 'nudge' | 'button' }
  | { type: 'closing-chat' }
  | { type: 'closing-nudge'; nextAction?: 'open-chat' };

interface WidgetState {
  main: WidgetMainState;
  transition: TransitionState;
  isNudgeDismissed: boolean;
}

type NudgeInput = {
  input: string;
  display?: string;
};

function getNudgeParams(
  nudgeInfo: NudgeInfo | undefined,
  nudgeInput: NudgeInput | undefined,
  nudgeId: string | undefined
) {
  if (!nudgeInfo || ('button' in nudgeInfo && !nudgeInfo.button)) {
    return {};
  }

  return {
    nudge: 'true',
    ...(nudgeInput?.input ? { nudgeInput: nudgeInput.input } : {}),
    ...(nudgeInput?.display ? { nudgeDisplay: nudgeInput.display } : {}),
    ...(nudgeId ? { nudgeId } : {}),
  };
}

const Root = styled('div')`
  position: relative;
  width: 0;
  height: 0;
`;

function WidgetViews({
  widgetConfig,
  embedderInfo,
  widgetAppParams,
  nudgeInfo,
  nudgeId,
  state,
  setState,
  onOpenRef,
  onCloseRef,
  authToken,
  hostElement,
}: WidgetViewsProps) {
  const [nudgeInput, setNudgeInput] = useState<NudgeInput | undefined>(undefined);
  const [initialMessage, setInitialMessage] = useState<string | undefined>(undefined);

  const isMobile = useIsMobile();
  const zIndexLayers = useMemo(
    () =>
      getWidgetZIndexLayers(
        widgetConfig.styles.zIndex,
        widgetConfig.styles.bubbleZIndex ?? widgetConfig.styles.zIndex
      ),
    [widgetConfig.styles.bubbleZIndex, widgetConfig.styles.zIndex]
  );

  const linkTarget = resolveLinkTargetSetting(widgetConfig.chat.linkTarget);

  const handleCloseChatWindow = () => {
    setInitialMessage(undefined);
    setState((prev) => ({
      ...prev,
      transition: { type: 'closing-chat' },
    }));

    setTimeout(
      () => {
        setState((prev) => ({
          main: widgetConfig.chat.isDrawer ? 'initial' : 'chat-minimized',
          transition: { type: 'idle' },
          isNudgeDismissed: prev.isNudgeDismissed,
        }));
      },
      widgetConfig.chat.isDrawer ? 300 : 320
    );
  };

  const handleNudgeDismiss = () => {
    setState((prev) => ({
      ...prev,
      isNudgeDismissed: true,
      transition: { type: 'idle' },
    }));
  };

  const handleNudgeInput = (input: string, display?: string) => {
    setNudgeInput({ input, display });

    setState((prev) => ({
      ...prev,
      transition: { type: 'closing-nudge', nextAction: 'open-chat' },
    }));

    setTimeout(() => {
      setState(() => ({
        main: 'chat-open',
        transition: { type: 'opening-chat', from: 'nudge' },
        isNudgeDismissed: true,
      }));

      setTimeout(() => {
        setState((prev) => ({
          ...prev,
          transition: { type: 'idle' },
        }));
      }, 380);
    }, 420);
  };

  const handleOpenChat = (message?: string) => {
    if (isChatOpen) {
      return;
    }

    if (message) {
      setInitialMessage(message);
    }

    if (!state.isNudgeDismissed && hasNudgeBox) {
      setState((prev) => ({
        ...prev,
        transition: { type: 'closing-nudge', nextAction: 'open-chat' },
      }));

      setTimeout(() => {
        setState(() => ({
          main: 'chat-open',
          transition: { type: 'opening-chat', from: 'button' },
          isNudgeDismissed: true,
        }));

        setTimeout(() => {
          setState((prev) => ({
            ...prev,
            transition: { type: 'idle' },
          }));
        }, 380);
      }, 420);
    } else {
      setState(() => ({
        main: 'chat-open',
        transition: { type: 'opening-chat', from: 'button' },
        isNudgeDismissed: true,
      }));

      setTimeout(() => {
        setState((prev) => ({
          ...prev,
          transition: { type: 'idle' },
        }));
      }, 380);
    }
  };

  const handleFabClick = () => {
    if (isChatOpen) {
      handleCloseChatWindow();
    } else {
      handleOpenChat();
    }
  };

  const chatUrl = useMemo(() => {
    const iframeParams = {
      widgetKey: widgetConfig.widgetKey,
      ...(widgetConfig.groupingId ? { groupingId: widgetConfig.groupingId } : {}),
      ...(widgetConfig.customContext
        ? { customContext: JSON.stringify(widgetConfig.customContext) }
        : {}),
      widgetAppName: widgetAppParams.widgetAppName,
      behaviorType: widgetAppParams.behaviorType,
      ...(widgetAppParams.requiredSSOName
        ? { requiredSSOName: widgetAppParams.requiredSSOName }
        : {}),
      ...(widgetAppParams.supportsJwt ? { supportsJwt: 'true' } : {}),

      primaryColor: widgetConfig.styles.primary,
      secondaryColor: widgetConfig.styles.secondary,
      tertiaryColor: widgetConfig.styles.tertiary,

      linkTarget,

      embedderInfo: JSON.stringify(embedderInfo),
      ...getNudgeParams(nudgeInfo, nudgeInput, nudgeId),
      ...(initialMessage ? { initialMessage } : {}),
      ...(shouldUseJwtAuth(widgetAppParams, authToken) ? { jwt: authToken } : {}),
    };

    const path = resolveIframePath(widgetAppParams, authToken);

    const url = new URL(path, widgetConfig.chatBaseUrl);
    url.search = new URLSearchParams(
      Object.entries(iframeParams).filter(([, v]) => v !== undefined && v !== null)
    ).toString();
    return url.toString();
  }, [
    widgetConfig,
    widgetAppParams,
    nudgeInfo,
    nudgeId,
    nudgeInput,
    initialMessage,
    authToken,
    linkTarget,
  ]);

  const hasNudgeButton = !!(nudgeInfo && 'button' in nudgeInfo && !!nudgeInfo.button);
  const hasNudgeBox = !!(nudgeInfo && 'box' in nudgeInfo && nudgeInfo.box);

  const isChatOpen = state.main === 'chat-open';
  const isOpeningChat = state.transition.type === 'opening-chat';
  const isClosingChat = state.transition.type === 'closing-chat';
  const isClosingNudge = state.transition.type === 'closing-nudge';

  const showChatWindow = isChatOpen || isOpeningChat || isClosingChat;

  useEffect(() => {
    if (showChatWindow) {
      hostElement.style.setProperty('--wn-z-index', String(widgetConfig.styles.zIndex));
    } else {
      hostElement.style.removeProperty('--wn-z-index');
    }
  }, [showChatWindow, hostElement, widgetConfig.styles.zIndex]);

  const showNudge =
    (state.main === 'initial' || isClosingNudge) && !state.isNudgeDismissed && hasNudgeBox;
  const showFab =
    (state.main !== 'chat-open' || (!widgetConfig.chat.isDrawer && !isMobile)) &&
    (state.isNudgeDismissed || hasNudgeButton || !nudgeInfo) &&
    !widgetConfig?.button?.isHidden;

  useEffect(() => {
    if (onOpenRef) {
      onOpenRef.current = handleOpenChat;
    }
    if (onCloseRef) {
      onCloseRef.current = handleCloseChatWindow;
    }
  }, [onOpenRef, onCloseRef, state, nudgeInfo]);

  return (
    <Root>
      <ChatWindow
        opening={isOpeningChat}
        closing={isClosingChat}
        onClose={handleCloseChatWindow}
        isMobile={isMobile}
        widgetConfig={widgetConfig}
        chatUrl={chatUrl}
        isVisible={showChatWindow}
        zIndexLayers={zIndexLayers}
      />

      {showFab && (
        <FabButton
          side={widgetConfig.styles.side}
          type="button"
          icon={widgetConfig.button.icon}
          buttonText={widgetConfig.button.text}
          widgetState={isChatOpen ? 'chat' : 'button'}
          isDrawer={widgetConfig.chat.isDrawer}
          opening={isOpeningChat}
          closing={isClosingChat}
          isColorsReversed={widgetConfig.button.isColorsReversed}
          alertCount={
            nudgeInfo && 'button' in nudgeInfo && typeof nudgeInfo.button === 'object'
              ? (nudgeInfo.button.alertCount as number) || 0
              : 0
          }
          onClick={handleFabClick}
          zIndexLayers={zIndexLayers}
        />
      )}

      {showNudge && (
        <NudgeBox
          side={widgetConfig.styles.side}
          nudgeInfo={nudgeInfo}
          buttonHasText={!!widgetConfig.button.text}
          maxWidthCenter={widgetConfig.nudgeBox?.maxWidthCenter}
          maxWidthSide={widgetConfig.nudgeBox?.maxWidthSide}
          maxWidthTooltip={widgetConfig.nudgeBox?.maxWidthTooltip}
          linkTarget={linkTarget}
          shouldClose={isClosingNudge}
          onDismiss={handleNudgeDismiss}
          onUserInput={handleNudgeInput}
          zIndexLayers={zIndexLayers}
        />
      )}
    </Root>
  );
}

const defaultState: WidgetState = {
  main: 'initial',
  transition: { type: 'idle' },
  isNudgeDismissed: false,
};

export default function Widget({
  widgetConfig,
  deviceId,
  hostElement,
}: {
  widgetConfig: WorknetWidgetConfig;
  deviceId: string;
  hostElement: HTMLElement;
}) {
  const [sessionData, setSessionDataState] = useState<WorknetWidgetSessionData>();
  const [_, setLoadError] = useState<string | undefined>();
  const [state, setState] = useSessionStore<WidgetState>(
    `worknet-widget:state:${widgetConfig.widgetKey}:${deviceId}`,
    defaultState
  );

  const embedderInfo = getClientEmbedderInfo(deviceId);
  const jwt = widgetConfig.jwt;
  const openRef = useRef<((message?: string) => void) | undefined>(undefined);
  const closeRef = useRef<(() => void) | undefined>(undefined);

  useEffect(() => {
    fetch(`${widgetConfig.apiBaseUrl}/admin/v1/public/widget_events/load`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(jwt ? { 'x-wn-jwt': jwt } : {}),
        'x-wn-widget-key': widgetConfig.widgetKey,
      },
      body: JSON.stringify({
        widgetKey: widgetConfig.widgetKey,
        customContext: widgetConfig.customContext || {},
        embedderInfo,
        nudge: state.main === 'initial' && !state.isNudgeDismissed,
      }),
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.message || `Authentication failed: ${response.statusText}`;
          throw new Error(errorMessage);
        }
        const data: LoadWidgetResponse = await response.json();
        const widgetAppParams = getWidgetAppParams(data.widget);

        if (widgetAppParams.supportsJwt && !jwt) {
          console.error('[Worknet Widget] JWT token required but not provided');
          return;
        }

        const chatPersistence = data.widget.chatPersistence ?? false;
        setState((prev) =>
          chatPersistence ? { ...prev, transition: defaultState.transition } : defaultState
        );

        setSessionDataState({
          initialLoadId: data.loadId,
          lastLoadId: data.loadId,
          widgetAppParams,
          nudgeInfo: data.nudge,
          nudgeId: data.nudgeId,
        });
        setLoadError(undefined);
      })
      .catch((error) => {
        console.error('[Worknet Widget] Load failed:', error);
        const errorMessage = error.message || 'Failed to load widget';
        setLoadError(errorMessage);
        setState(defaultState);
        const onDemoError = (window as { __worknetDemoOnLoadError?: (msg: string) => void })
          .__worknetDemoOnLoadError;
        if (typeof onDemoError === 'function') {
          onDemoError(errorMessage);
        }
      });
  }, [widgetConfig.apiBaseUrl, widgetConfig.widgetKey, widgetConfig.jwt]);

  useEffect(() => {
    if (!sessionData) {
      return;
    }

    const windowConfig = (
      window as {
        __WorknetWidget?: { onReady?: (widget: WidgetAPI) => void };
      }
    ).__WorknetWidget;
    const onReady = windowConfig?.onReady;

    if (onReady && typeof onReady === 'function') {
      const widgetAPI: WidgetAPI = {
        open: (opts) => {
          if (openRef.current) {
            openRef.current(opts?.message);
          }
        },
        close: () => {
          if (closeRef.current) {
            closeRef.current();
          }
        },
      };
      setTimeout(() => {
        onReady(widgetAPI);
      }, 0);
    }
  }, [sessionData]);

  if (!sessionData) {
    return null;
  }

  return (
    <WidgetViews
      widgetConfig={widgetConfig}
      widgetAppParams={sessionData.widgetAppParams}
      embedderInfo={embedderInfo}
      nudgeInfo={sessionData.nudgeInfo}
      nudgeId={sessionData.nudgeId}
      state={state}
      setState={setState}
      onOpenRef={openRef}
      onCloseRef={closeRef}
      authToken={jwt}
      hostElement={hostElement}
    />
  );
}
